import { handleError } from '../utils/api-error-handling';
import { encryptPayload, hashSignature } from '../utils/security';
import { IPropertySubscription } from '@interfaces/property.interface';
import { createApi } from '@reduxjs/toolkit/query/react';
import { isHydrateAction } from '@services/hydration';
import { persistKey, store } from '@store/index';
import { baseUrl } from '@utils/url';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { IEmailRequestTraining } from '@components/dialog/dialog-request-for-training';
import { generalParameterApi } from './genpar.api.rtk';

export const propertyApi = createApi({
  reducerPath: 'propertyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrl,
    mode: 'cors',
    credentials: 'include',
    prepareHeaders: async (headers) => {
      const { getState } = store;
      const data = getState();

      if (data) {
        headers.set('x-access-token', data.user.info.token);
      }
      return headers;
    },
  }),
  extractRehydrationInfo(action, { reducerPath }): any {
    try {
      if (isHydrateAction(action)) {
        if (
          action.key === persistKey &&
          action.payload &&
          action.payload[reducerPath]
        ) {
          return action.payload[reducerPath];
        }
      }
    } catch (error) {
      console.error('Error extracting rehydration info:', error);
    }
    return undefined;
  },
  tagTypes: ['Properties'],
  endpoints: (builder) => ({
    getAllProperties: builder.query({
      query: () => {
        return {
          url: 'properties',
        };
      },
      providesTags: ['Properties'],
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0,
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getSubscriptions: builder.query({
      query: ({ propertyID }) => ({
        url: 'properties/subscriptions',
        headers: { propertyID },
      }),
      providesTags: ['Properties'],
      forceRefetch() {
        return true;
      },
      keepUnusedDataFor: 0,
      async onQueryStarted(_arg, { queryFulfilled }) {
        await queryFulfilled;
      },
      transformResponse: (response: IPropertySubscription) => {
        return response?.data;
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return {
          message: baseQueryReturnValue?.data?.data?.error_message,
          errors: baseQueryReturnValue?.data?.data?.errors?.map(
            ({ error }) => error?.remarks,
          ),
        };
      },
    }),
    updateSelfRegistrationStatus: builder.mutation({
      query: (payload) => ({
        url: 'properties/selfRegistration',
        method: 'PUT',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['Properties'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    postUserModule: builder.mutation({
      query: (param: string) => ({
        url: `authentication/user_module/${param}`,
        method: 'POST',
      }),
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    createProperty: builder.mutation({
      query: (payload) => ({
        url: 'properties',
        method: 'POST',
        body: payload,
        headers: {
          'user-api-key': import.meta.env.VITE_APP_CREATE_PROPERTY_KEY,
        },
      }),
      invalidatesTags: ['Properties'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    generateURL: builder.mutation({
      query: (payload) => ({
        url: 'properties/generate-url',
        method: 'POST',
        body: payload,
        headers: {
          'user-api-key': import.meta.env.VITE_APP_CREATE_PROPERTY_KEY,
        },
      }),
      invalidatesTags: ['Properties'],
      transformResponse: async (response: any) => {
        window.location.href = response.data.url;
        return response;
      },
      transformErrorResponse: async (error: any) => {
        return error?.data?.data?.errors
          ?.map(({ error }) => error?.remarks)
          ?.join(', ');
      },
    }),
    createContactPersonBilling: builder.mutation({
      query: ({
        propertyID,
        ...payload
      }: {
        lastName: string;
        firstName: string;
        middleName?: string; // Optional since it's an empty string
        email: string;
        contact: string;
        propertyID: string;
      }) => ({
        url: 'properties/billing-information',
        method: 'POST',
        body: {
          ...payload,
          signature: hashSignature(
            [
              payload.lastName,
              payload.firstName,
              payload.middleName,
              payload.email,
              payload.contact,
            ].join(''),
          ),
        },
        headers: {
          propertyID,
        },
      }),
      invalidatesTags: ['Properties'],
      transformResponse: async (response) => {
        return response;
      },
      transformErrorResponse: async (error: any) => {
        return error?.data?.data?.errors
          ?.map(({ error }) => error?.remarks)
          ?.join(', ');
      },
    }),
    updatePropertyLogo: builder.mutation({
      query: ({ propertyID, ...payload }) => ({
        url: `properties/${propertyID}/logo`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Properties'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    updateProperty: builder.mutation({
      query: ({ propertyID, ...body }) => ({
        url: `properties/${propertyID}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data) {
          dispatch(generalParameterApi.util.invalidateTags(['GENPAR']));
        }
      },
    }),
    emailRequestTraining: builder.mutation({
      query: (payload: IEmailRequestTraining) => ({
        url: 'properties/email-request-training',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetAllPropertiesQuery,
  useLazyGetAllPropertiesQuery,
  useGetSubscriptionsQuery,
  useLazyGetSubscriptionsQuery,
  useUpdateSelfRegistrationStatusMutation,
  usePostUserModuleMutation,
  useCreatePropertyMutation,
  useGenerateURLMutation,
  useCreateContactPersonBillingMutation,
  useUpdatePropertyLogoMutation,
  useUpdatePropertyMutation,
  useEmailRequestTrainingMutation,
} = propertyApi;
